<template>
  <v-container fluid class="integrateform-container pa-0 ma-0">
    <!-- Modern Header Section -->
    <div
      v-if="showHeader"
      class="header-section mb-6"
      style="margin: 0 12px; position: relative;"
    >
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-form-select</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-1">Web Forms</h1>
          <p class="page-subtitle mb-0">
            Embed custom forms in your website and get leads from it
          </p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex">
          <v-chip
            small
            class="mr-2"
            color="rgba(255,255,255,0.2)"
            text-color="white"
          >
            <v-icon small left>mdi-web</v-icon>
            Lead Generation
          </v-chip>
        </div>
      </div>
      <v-row class="feature-pills ma-0">
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-code-tags</v-icon>
            <span>Embed Code</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-palette</v-icon>
            <span>Customizable</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-lightning-bolt</v-icon>
            <span>Instant Leads</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="pa-1">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-responsive</v-icon>
            <span>Responsive</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Content Section -->
    <div style="padding: 0 12px;">
      <v-card
        fluid
        flat
        tile
        class="white-background pa-6 px-md-12 customBorderRadius elevation-2"
      >
        <v-row>
          <v-col cols="12" md="7">
            <v-card
              class="modern-card fill-height customBorderRadius"
              elevation="0"
            >
              <div class="card-header">
                <div class="icon-badge">
                  <v-icon color="white" size="20">mdi-hammer-wrench</v-icon>
                </div>
                <h3 class="card-title">Form Builder</h3>
              </div>
              <v-divider></v-divider>
              <v-card-text class="pa-6">
                <v-autocomplete
                  v-model="selectedList"
                  class="mb-4 customBorderRadius"
                  :items="lists"
                  item-text="name"
                  item-value="id"
                  label="Lists"
                  autocomplete="false"
                  prepend-inner-icon="view_list"
                  hide-details
                  dense
                  outlined
                  @change="generateHtml()"
                />

                <label class="input-label">Form Title</label>
                <v-text-field
                  v-model="form.title"
                  hide-details
                  class="mb-4 customBorderRadius"
                  outlined
                  dense
                  @change="generateHtml()"
                />

                <label class="input-label">Form Sub Title</label>
                <v-text-field
                  v-model="form.subTitle"
                  hide-details
                  class="mb-4 customBorderRadius"
                  outlined
                  dense
                  @change="generateHtml()"
                />

                <label class="input-label mb-2">Fields in Form</label>
                <v-row class="mt-0">
                  <v-col
                    v-for="(item, i) in fields"
                    :key="i"
                    cols="12"
                    md="6"
                    class="py-1"
                  >
                    <v-checkbox
                      :value="form.fields.includes(item)"
                      :label="item.name"
                      dense
                      hide-details
                      color="primary"
                      @change="updateFieldList(item)"
                    />
                  </v-col>
                </v-row>

                <v-checkbox
                  v-model="form.legal"
                  label="Legal Text"
                  class="mt-2 mb-2"
                  hide-details
                  color="primary"
                  @change="generateHtml()"
                ></v-checkbox>

                <v-textarea
                  v-model="form.legalText"
                  :disabled="!form.legal"
                  auto-grow
                  rows="2"
                  outlined
                  dense
                  hide-details
                  class="mb-4 customBorderRadius"
                  @change="generateHtml()"
                />

                <label class="input-label">Button Text</label>
                <v-text-field
                  v-model="form.buttonText"
                  outlined
                  dense
                  hide-details
                  class="mb-4 customBorderRadius"
                  @change="generateHtml()"
                />

                <label class="input-label">Form Width</label>
                <v-text-field
                  v-model="form.width"
                  outlined
                  dense
                  hide-details
                  suffix="px"
                  class="mb-4 customBorderRadius"
                  @change="generateHtml()"
                />

                <label class="input-label">Redirect URL *</label>
                <v-text-field
                  v-model="form.redirect"
                  hide-details
                  class="mb-4 customBorderRadius"
                  outlined
                  dense
                />

                <label class="input-label mb-3">Color</label>
                <div class="d-flex mb-5 flex-wrap">
                  <v-avatar
                    v-for="color in colors"
                    :key="color"
                    :color="color"
                    class="ma-1 color-avatar"
                    :class="{ 'active-border': form.color === color }"
                    size="32"
                    style="cursor: pointer;"
                    @click="
                      form.color = color;
                      generateHtml();
                    "
                  >
                    <v-icon v-if="form.color === color" color="white" size="18"
                      >mdi-check</v-icon
                    >
                  </v-avatar>
                </div>

                <label class="input-label mb-2">Theme</label>
                <v-radio-group
                  v-model="form.theme"
                  row
                  class="mt-1"
                  hide-details
                  @change="generateHtml()"
                >
                  <v-radio
                    label="Light"
                    :value="theme[0]"
                    color="primary"
                  ></v-radio>
                  <v-radio
                    label="Dark"
                    :value="theme[1]"
                    color="primary"
                  ></v-radio>
                </v-radio-group>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="5">
            <v-card
              class="modern-card fill-height customBorderRadius"
              elevation="0"
            >
              <div class="card-header">
                <div class="icon-badge">
                  <v-icon color="white" size="20">mdi-eye-outline</v-icon>
                </div>
                <h3 class="card-title">Form Preview</h3>
              </div>
              <v-divider></v-divider>
              <v-card-text class="pa-6">
                <v-card
                  :dark="form.theme == 'dark'"
                  class="preview-form"
                  elevation="2"
                >
                  <v-card-text class="pa-5">
                    <h2 class="mb-2">{{ form.title }}</h2>
                    <span class="text-caption">{{ form.subTitle }}</span>
                    <v-divider
                      :color="form.color"
                      class="pt-1 mt-3 mb-4"
                    ></v-divider>
                    <div v-for="(field, i) in form.fields" :key="i">
                      <v-text-field
                        disabled
                        :label="field.name"
                        :placeholder="`Enter your ${field.name}`"
                        hide-details
                        class="mb-4"
                        outlined
                        dense
                      />
                    </div>
                    <span v-if="form.legal">
                      <v-checkbox
                        :color="form.color"
                        :label="form.legalText"
                        hide-details
                        class="mt-0"
                      ></v-checkbox>
                    </span>
                    <div class="d-flex justify-end mt-4">
                      <v-btn
                        :color="form.color"
                        dark
                        depressed
                        style="border-radius: 8px;"
                      >
                        {{ form.buttonText }}
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="modern-card mb-4 customBorderRadius" elevation="0">
              <div class="card-header">
                <div class="icon-badge">
                  <v-icon color="white" size="20">mdi-code-tags</v-icon>
                </div>
                <h3 class="card-title">HTML Code</h3>
                <v-spacer></v-spacer>
                <v-btn
                  depressed
                  color="primary"
                  style="border-radius: 8px;"
                  @click="copy(htmlCode)"
                >
                  <v-icon left size="18">mdi-content-copy</v-icon>
                  Copy
                </v-btn>
              </div>
              <v-divider></v-divider>
              <v-card-text class="code-wrapper pa-0">
                <pre class="code-content"><code>{{ htmlCode }}</code></pre>
              </v-card-text>
            </v-card>

            <v-card class="modern-card customBorderRadius" elevation="0">
              <div class="card-header">
                <div class="icon-badge">
                  <v-icon color="white" size="20">mdi-language-css3</v-icon>
                </div>
                <h3 class="card-title">CSS Code</h3>
                <v-spacer></v-spacer>
                <v-btn
                  depressed
                  color="primary"
                  style="border-radius: 8px;"
                  @click="copy(cssCode)"
                >
                  <v-icon left size="18">mdi-content-copy</v-icon>
                  Copy
                </v-btn>
              </div>
              <v-divider></v-divider>
              <v-card-text class="code-wrapper pa-0">
                <pre class="code-content"><code>{{ cssCode }}</code></pre>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  data: () => ({
    showHeader: false,
    tab: "",
    lists: [],
    selectedList: "",
    fields: [],
    selectedFields: [],
    colors: [
      "red",
      "pink",
      "purple",
      "indigo",
      "blue",
      "green",
      "yellow",
      "amber",
      "brown",
      "black",
    ],
    theme: ["light", "dark"],
    form: {
      title: "Untitled",
      subTitle: "Subtitle",
      fields: [],
      legal: false,
      legalText:
        "By clicking I am aware of the terms and condition and privacy policy",
      buttonText: "Submit",
      width: "500",
      redirect: "https://godial.cc",
      color: "black",
      theme: "light",
    },
    htmlCode: "",
    cssCode: "",
    token: "",
  }),
  computed: {
    ...mapGetters(["ENDPOINT", "fieldList", "teams", "selectedTeam"]),
  },
  watch: {
    async selectedTeam() {
      try {
        this.selectedList = "";
        this.getAllFields();
        await this.fetchLists(); // fetch list
        this.refresh();
      } catch (e) {
        console.log(e);
      }
    },
  },
  created() {
    this.showHeader =
      localStorage.getItem("integrateFormHeaderDismissed") !== "true";
    this.fetchLists();
    this.getAllFields();
    this.getAccessToken();
  },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("integrateFormHeaderDismissed", "true");
    },
    getAccessToken() {
      const self = this;
      var aToken = self.$store.getters.user.token;
      self.$http
        .get(`${this.ENDPOINT}/externals/token?access_token=${aToken}`)
        .then((response) => {
          self.token = response.body.id;
          self.generateHtml();
        })
        .catch((err) => {
          self.$swal({ type: "error", text: err.body.error.message });
        });
    },
    getTeamIds() {
      var teams = [];
      if (this.selectedTeam == "allTeams") {
        teams = _.map(this.teams, "id");
      } else {
        teams.push(this.selectedTeam);
      }

      teams = _.without(teams, "allTeams");

      return teams;
    },
    async fetchLists() {
      try {
        this.lists = [];
        var response = [];
        var teamIds = this.getTeamIds();

        // Teams sections
        response = await this.$http.post(`${this.ENDPOINT}/lists/fetch`, {
          teams: teamIds,
        });
        this.lists = response.body;
        if (this.lists.length < 1) {
          return;
        }
        this.selectedList = this.lists[0].id;
      } catch (e) {
        console.log("error in list fetch", e);
      }
    },
    getAllFields() {
      // Set Custom Field in
      var tempFields = [
        { name: "Name", value: "name" },
        { name: "Company Name", value: "companyName" },
        { name: "Email", value: "email" },
        { name: "Phone", value: "phone" },
        { name: "Secondary Phone", value: "secondPhone" },
        { name: "Extra", value: "extra" },
        { name: "Remarks", value: "remarks" },
        { name: "Note", value: "note" },
      ];

      // if (this.fieldList.length > 1) {
      //     var fieldType = "";
      //     this.fieldList.forEach((field) => {
      //     if (
      //         field.type == "text" ||
      //         field.type == "" ||
      //         field.type == "mcq" ||
      //         field.type == "checkbox"
      //     ) {
      //         fieldType = "customField";
      //     } else if (field.type == "number") {
      //         fieldType = "customFieldNumber";
      //     } else if (field.type == "date") {
      //         fieldType = "customFieldDate";
      //     }

      //     tempFields.push({
      //         name: field.name,
      //         value: field.name,
      //         fieldType: fieldType,
      //     });
      //     });
      // }
      this.fields = tempFields;
      // console.log("tempFields: ",this.fields);
    },
    async refresh() {},
    updateFieldList(item) {
      // console.log(this.form.fields.includes(item));
      if (!this.form.fields.includes(item)) this.form.fields.push(item);
      else
        this.form.fields = this.form.fields.filter((f) => {
          return f !== item;
        });
      // console.log(this.form.fields);
      this.generateHtml();
    },
    generateHtml() {
      var legalText = this.form.legalText;
      var fieldHTML = this.form.fields
        .map((field) => {
          return `<fieldset class="go-text-field"><input type="text" placeholder="${field.name}" name="${field.value}"></fieldset>`;
        })
        .join("");
      var basic = `<div class="go-flex">
                <form class="go-card go-theme" 
			    action="https://enterprise.godial.cc/meta/api/externals/contact/add?access_token=${
            this.token
          }" method="POST">
				<div class="go-card-title">
					<h2 class="go-title">${this.form.title}</h2>
					<span class="go-subtitle">${this.form.subTitle}</span>
				</div>
				<hr class="go-divider accent-color">
				<div class="go-card-text">
					<input type="hidden" name="listId" value="${this.selectedList}">
                    <input type="hidden" name="redirect" value="${
                      this.form.redirect
                    }">
                    ${fieldHTML}
                    ${
                      this.form.legal
                        ? `<fieldset class="go-legal">
						<input class="accent-color" type="checkbox" name="agreement">
						<div class="go-label">
							${legalText}
						</div>
				</fieldset>`
                        : ""
                    }
					
				</div>
				<div class="go-card-actions">
					<button class="accent-color" type="submit">${this.form.buttonText}
					</button>
				</div>
			</form>
            </div>`;
      this.htmlCode = basic;
      this.generateCss();
    },
    generateCss() {
      var colorMap = {
        red: "#F44336",
        pink: "#E91E63",
        purple: "#9C27B0",
        indigo: "#3F51B5",
        blue: "#03A9F4",
        green: "#8BC34A",
        yellow: "#FFEB3B",
        amber: "#FFC107",
        brown: "#795548",
        black: "#000",
      };
      var cssBasic = `<style>
.go-flex{
    display:flex;
    justify-content:center;
}
.go-theme{
    background:${this.form.theme == "dark" ? `#292929` : `#fff`};
    color:${this.form.theme == "dark" ? `#e6e6e6` : `#555555`};
}
.go-theme input{
    color:${this.form.theme == "dark" ? `#e6e6e6` : `#3f3f3f`};
}
.go-card{
    box-shadow: 1px 1px 15px #83838355;
    margin:12px;
    padding:12px 16px;
    border-radius: 4px;
    width: ${this.form.width == "" ? 345 : `${this.form.width}px`};
    font-family: Roboto, 'Segoe UI', system-ui, Oxygen,'Open Sans', sans-serif;
}
.go-title{
    margin:5px 0px;
}
.go-subtitle{
    color:#797979;
}
.go-card-text{
    padding:10px 0;
}
.go-text-field{
    border:1px solid #555555;
    border-radius:3px;
    margin-bottom:12px;
    height:20px;
}
.go-text-field input{
    background-color: transparent;
    width: 100%;
    font-size:18px;
    font-family: Roboto, 'Segoe UI', system-ui, Oxygen,'Open Sans', sans-serif;
    border:none;
    outline: none;
}
.go-legal{
    border:none;
    padding:0;
    display: flex;
}
.go-legal .go-label{
    margin-left:10px;
}
.go-legal .go-label a{
    font-size: 16px;
    font-weight:300;
    text-decoration: none;
    color:inherit;
}
.go-card-actions{
    display: flex;
    flex-direction: row-reverse;
}
.accent-color{
    background-color: ${colorMap[this.form.color]};
    accent-color:${colorMap[this.form.color]};
}
.go-divider{
    height:4px;
    border:none;
}
.go-legal input{
    height:15px;
    width:15px;
}
.go-card-actions button{
    border:none;
    border-radius: 2px;
    padding: 10px 20px;
    color:#fff;
    text-transform: uppercase;
    font-size: 15px;
    font-family: Roboto, 'Segoe UI', system-ui, Oxygen,'Open Sans', sans-serif;
    font-weight: 500;
}
</style>`;
      this.cssCode = cssBasic;
    },
    copy(text) {
      const self = this;
      this.$copyText(text).then(console.log("Copied"));
    },
  },
};
</script>

<style scoped>
.white-background {
  background-color: #ffffff;
  min-height: 100vh;
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
}

.icon-wrapper {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: white;
  margin: 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

/* Modern Cards */
.modern-card {
  border-radius: 12px !important;
  border: 1px solid #e0e0e0 !important;
  overflow: hidden;
}

.card-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  display: flex;
  align-items: center;
  gap: 12px;
}

.icon-badge {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  letter-spacing: 0.5px;
}

/* Input Labels */
.input-label {
  font-size: 14px;
  font-weight: 500;
  color: #424242;
  margin-bottom: 8px;
  display: block;
}

/* Custom Border Radius */
.customBorderRadius fieldset {
  border-radius: 8px !important;
}

.customBorderRadius .v-input__slot {
  border-radius: 8px !important;
  overflow: hidden;
}

/* Color Avatars */
.color-avatar {
  cursor: pointer;
  border: 3px solid transparent;
  transition: all 0.2s ease;
}

.color-avatar:hover {
  transform: scale(1.1);
}

.active-border {
  border-color: #37cf37 !important;
  box-shadow: 0 2px 8px rgba(55, 207, 55, 0.3);
}

/* Preview Form */
.preview-form {
  border-radius: 12px !important;
}

/* Code Wrapper */
.code-wrapper {
  background: #f5f5f5;
  max-height: 300px;
  overflow-y: auto;
}

.code-content {
  margin: 0;
  padding: 16px 20px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #424242;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Custom Scrollbar */
.code-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.code-wrapper::-webkit-scrollbar-track {
  background: #e0e0e0;
  border-radius: 4px;
}

.code-wrapper::-webkit-scrollbar-thumb {
  background: #37cf37;
  border-radius: 4px;
}

.code-wrapper::-webkit-scrollbar-thumb:hover {
  background: #2ba82b;
}

/* Responsive */
@media (max-width: 960px) {
  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
  }
}

.integrateform-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .integrateform-container {
  background-color: #121212 !important;
}

/* Dark Theme Styles */
.theme--dark .white-background {
  background-color: #1e1e1e !important;
}

.theme--dark .modern-card {
  border-color: #424242 !important;
  background-color: #2d2d2d !important;
}

.theme--dark .input-label {
  color: #e0e0e0 !important;
}

.theme--dark .code-wrapper {
  background: #1a1a1a !important;
}

.theme--dark .code-content {
  color: #e0e0e0 !important;
}

.theme--dark .code-wrapper::-webkit-scrollbar-track {
  background: #2d2d2d;
}

.theme--dark .preview-form {
  background-color: #2d2d2d !important;
}
</style>
